// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import actionCreators from "@modules/service/actionCreator";
import type {
  DynamicQuestionsResponse,
  DynamicQuestionContent,
} from "@modules/service/reducer/dynamic-questions/types";
import type {
  HierarchyAssignment,
  ApprovableContainer,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import {
  GetDynamicQuestions,
  AddNewDynamicQuestion,
  GetDynamicQuestion,
  UpdateDynamicQuestion,
  UpdateDynamicQuestionAssignments,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { sortToString, transformSort } from "@transformers/sort";
type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

type DynamicQuestionsListRequest = $Exact<{
  clientId: string,
  isActive?: boolean,
  page: number,
  search: Object,
  sort: Object,
}>;

type AddDynamicQuestionsRequest = $Exact<{
  clientId: string,
  draft: DynamicQuestionContent,
  assignments: Array<HierarchyAssignment>,
  filter: string,
}>;

export type DynamicQuestionRequest = $Exact<{
  ...DynamicQuestionsListRequest,
  stableId: string,
}>;

type DynamicQuestionUpdateRequest = $Exact<{
  ...DynamicQuestionRequest,
  draft: DynamicQuestionContent,
}>;

export function* onGetDynamicQuestions({
  payload,
}: Action<DynamicQuestionsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<DynamicQuestionsResponse>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question`,
      {
        params: {
          limit: 0,
          page: payload.page ? payload.page : 0,
          sort: payload.sort ? sortToString(transformSort(payload.sort)) : "",
          isActive: payload.isActive !== null ? !!payload.isActive : null,
        },
      },
    );

    yield put(actionCreators.GetDynamicQuestionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetDynamicQuestionsError(e.response));
  }
}

export function* onAddNewDynamicQuestion({
  payload,
}: Action<AddDynamicQuestionsRequest>): Generator<*, void, { data: DynamicQuestionContent }> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.GetDynamicQuestionSuccess(response.data)),
      put(
        actionCreators.GetDynamicQuestions({
          clientId: payload.clientId,
          isActive: payload.filter !== "ALL" ? payload.filter == "ACTIVE" : null,
        }),
      ),
      put(actionCreators.SetShowAddDynamicQuestionModal({ show: false, step: 1 })),
      put(sendSuccessMessage("DYNAMIC_QUESTION_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.GetDynamicQuestionError(e.response));
  }
}

export function* onGetDynamicQuestion({
  payload,
}: Action<DynamicQuestionRequest>): Generator<*, void, { data: DynamicQuestionContent }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const stableResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}/assignment`,
    );

    yield put(actionCreators.GetDynamicQuestionSuccess(stableResponse.data));
    yield put(actionCreators.SetDynamicQuestionAssignments(assignmentsResponse.data));
  } catch (e) {
    yield put(actionCreators.GetDynamicQuestionError(e.response));
  }
}

export function* onUpdateDynamicQuestion({
  payload,
}: Action<DynamicQuestionUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<DynamicQuestionContent>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/dynamic-question/question/${payload.stableId}`,
      payload.draft,
    );

    yield all([
      put(actionCreators.UpdateDynamicQuestionSuccess(response.data)),
      put(actionCreators.ShowDynamicQuestionEditModal({ show: false })),
    ]);
    yield put(sendSuccessMessage("DYNAMIC_QUESTION_UPDATED"));
  } catch (e) {
    yield put(actionCreators.UpdateDynamicQuestionError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  stableId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    yield put(actionCreators.GetDynamicQuestions({ clientId: params.clientId }));
    if (params.stableId) {
      yield put(
        actionCreators.GetDynamicQuestion({ clientId: params.clientId, stableId: params.stableId }),
      );
    }
  } catch (e) {
    throw e;
  }
}

type DynamicQuestionUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<DynamicQuestionContent>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateDynamicQuestionAssignments({
  payload,
}: Action<DynamicQuestionUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/dynamic-question/question/${payload.container.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/dynamic-question/question/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/dynamic-question/question/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      stableId: payload.container.id,
    });

    yield all([
      put(actionCreators.ShowEditDynamicQuestionAssignmentsModal(false)),
      put(
        push(
          route("ADMINISTRATION_CLIENT_FEATURES_PATH", {
            clientId: payload.container.clientId,
          }),
        ),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateDynamicQuestionAssignmentsError(e.message));
  }
}

export function* DynamicQuestionsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetDynamicQuestions, onGetDynamicQuestions),
    takeEvery(AddNewDynamicQuestion, onAddNewDynamicQuestion),
    takeEvery(GetDynamicQuestion, onGetDynamicQuestion),
    takeEvery(UpdateDynamicQuestion, onUpdateDynamicQuestion),
    takeEvery(UpdateDynamicQuestionAssignments, onUpdateDynamicQuestionAssignments),
  ]);
}

export default DynamicQuestionsSagas;
